import type { ComponentType } from 'react';
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  View,
  type PressableProps,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import { colors, fonts, fontSizes, radii, shadows, spacing } from '@/constants/theme';

type Variant = 'primary' | 'dark' | 'outline' | 'outlineOnDark' | 'ghost';
type Size = 'sm' | 'base' | 'lg';

type IconProps = { color: string; size: number };

export interface ButtonProps extends Omit<PressableProps, 'style' | 'children'> {
  label: string;
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  fullWidth?: boolean;
  leadingIcon?: ComponentType<IconProps>;
  trailingIcon?: ComponentType<IconProps>;
  style?: StyleProp<ViewStyle>;
}

/**
 * Primary CTA = `primary` (Electric Green pill, Deep Green text). Codified per
 * brand kit: never invent a color combo for the primary action — always
 * Electric Green pill with the ctaGlow shadow.
 *
 * Sized via `size` (sm/base/lg). Disabled state is visually neutral grey,
 * not just opacity 0.5, so users read it as "you can't tap this yet" rather
 * than "this button looks broken".
 */
export function Button({
  label,
  variant = 'primary',
  size = 'base',
  loading,
  fullWidth,
  leadingIcon: LeadingIcon,
  trailingIcon: TrailingIcon,
  disabled,
  style,
  ...rest
}: ButtonProps) {
  const palette = PALETTES[variant];
  const sizing = SIZES[size];
  const isDisabled = disabled || loading;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ disabled: !!isDisabled, busy: !!loading }}
      disabled={isDisabled}
      style={({ pressed }) => [
        styles.base,
        palette.container,
        sizing.container,
        fullWidth && styles.fullWidth,
        isDisabled && styles.disabled,
        isDisabled && palette.disabledContainer,
        pressed && !isDisabled && styles.pressed,
        style,
      ]}
      {...rest}
    >
      {loading ? (
        <ActivityIndicator color={palette.text.color} size="small" />
      ) : (
        <View style={styles.row}>
          {LeadingIcon && (
            <LeadingIcon color={palette.text.color} size={sizing.iconSize} />
          )}
          <Text
            style={[
              styles.text,
              palette.text,
              sizing.text,
              isDisabled && palette.disabledText,
            ]}
            numberOfLines={1}
            // Labels used to CLIP under iOS Dynamic Type — "Send code" became
            // "Send co…" on a device with Larger Text, and the campaign's
            // "Claim my free gift" / "Claim <product>" are longer still, so the
            // primary conversion button was the most exposed. numberOfLines={1}
            // is deliberate (a wrapped button label looks broken), so the label
            // scales down to fit instead of truncating. minimumFontScale keeps
            // it legible rather than shrinking without bound.
            adjustsFontSizeToFit
            minimumFontScale={0.85}
          >
            {label}
          </Text>
          {TrailingIcon && (
            <TrailingIcon color={palette.text.color} size={sizing.iconSize} />
          )}
        </View>
      )}
    </Pressable>
  );
}

const PALETTES = {
  primary: {
    container: {
      backgroundColor: colors.electricGreen,
      ...shadows.ctaGlow,
    },
    text: { color: colors.deepGreen },
    disabledContainer: {
      backgroundColor: colors.border,
      shadowOpacity: 0,
      elevation: 0,
    },
    disabledText: { color: colors.textMuted },
  },
  dark: {
    container: { backgroundColor: colors.deepGreen },
    text: { color: colors.frost },
    disabledContainer: { backgroundColor: colors.border, shadowOpacity: 0 },
    disabledText: { color: colors.textMuted },
  },
  outline: {
    container: {
      backgroundColor: 'transparent',
      borderWidth: 1.5,
      borderColor: colors.deepGreen,
    },
    text: { color: colors.deepGreen },
    disabledContainer: { borderColor: colors.border },
    disabledText: { color: colors.textMuted },
  },
  // Outline for DARK (Deep Green) surfaces — `outline` disappears there
  // (deep-green border on deep-green card). Matches the handoff's on-dark
  // secondary button: 25%-frost border, frost text.
  outlineOnDark: {
    container: {
      backgroundColor: 'transparent',
      borderWidth: 1.5,
      borderColor: colors.pinCellOnDarkBorder,
    },
    text: { color: colors.frost },
    disabledContainer: { borderColor: colors.glassFrostBorder },
    disabledText: { color: colors.textOnDarkDisabled },
  },
  ghost: {
    container: { backgroundColor: 'transparent' },
    text: { color: colors.deepGreen },
    disabledContainer: {},
    disabledText: { color: colors.textMuted },
  },
} as const;

const SIZES = {
  sm: {
    container: { paddingVertical: spacing.sm, paddingHorizontal: spacing.md },
    text: { fontSize: fontSizes.sm },
    iconSize: 14,
  },
  base: {
    container: { paddingVertical: spacing.base, paddingHorizontal: spacing.lg },
    text: { fontSize: fontSizes.base },
    iconSize: 16,
  },
  lg: {
    container: { paddingVertical: spacing.base + 2, paddingHorizontal: spacing.xl },
    text: { fontSize: fontSizes.lg },
    iconSize: 18,
  },
} as const;

const styles = StyleSheet.create({
  base: {
    borderRadius: radii.full,
    alignItems: 'center',
    justifyContent: 'center',
  },
  fullWidth: { width: '100%' },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  text: {
    fontFamily: fonts.bodyBold,
    letterSpacing: 0.3,
  },
  pressed: {
    transform: [{ scale: 0.97 }],
    opacity: 0.92,
  },
  disabled: {},
});
